import { useEffect, useState } from "react";
import { Search, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { fuzzyMatch } from "../../lib/searchUtils";

const byName = (a, b) => (a.name || "").localeCompare(b.name || "");

const EmployeeButton = ({ emp, selected, onPick, testId }) => (
  <Button variant="outline" className="w-full justify-between h-auto py-3 text-left" onClick={() => onPick(emp.id)} data-testid={testId}>
    <div>
      <p className="font-medium">{emp.name}</p>
      {emp.role && <p className="text-xs text-muted-foreground">{emp.role}</p>}
    </div>
    {selected && <Check className="w-4 h-4 shrink-0" />}
  </Button>
);

// Pop-up employee picker (search + browse by role), same pattern as the material picker.
const EmployeePickerDialog = ({ open, onOpenChange, employeesByRole = {}, value, onSelect, testIdPrefix = "employee-select" }) => {
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  useEffect(() => { if (!open) { setSearch(""); setRole(""); } }, [open]);

  const all = Object.entries(employeesByRole).flatMap(([r, emps]) => emps.map((e) => ({ ...e, role: r })));
  const results = search.trim() ? all.filter((e) => fuzzyMatch(search, e.name || "")).sort(byName) : [];
  const roles = Object.keys(employeesByRole);
  const pick = (id) => { onSelect(id); onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Select Employee</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search employees..." value={search} onChange={(e) => { setSearch(e.target.value); if (e.target.value) setRole(""); }} className="pl-9" autoFocus data-testid={`${testIdPrefix}-search`} />
        </div>
        {search.trim() ? (
          <div className="grid gap-2 max-h-[50vh] overflow-y-auto" style={{ overscrollBehavior: "contain" }}>
            {results.length ? results.map((e) => <EmployeeButton key={e.id} emp={e} selected={e.id === value} onPick={pick} testId={`${testIdPrefix}-option-${e.id}`} />)
              : <p className="text-center text-muted-foreground py-4">No employees matching "{search}"</p>}
          </div>
        ) : !role ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Or browse by role:</p>
            <div className="grid gap-2 max-h-[50vh] overflow-y-auto" style={{ overscrollBehavior: "contain" }}>
              {roles.length ? roles.map((r) => (
                <Button key={r} variant="outline" className="w-full justify-between h-12 text-left" onClick={() => setRole(r)} data-testid={`${testIdPrefix}-role-${r}`}>
                  <span className="font-medium">{r}</span>
                  <span className="text-muted-foreground text-sm">{employeesByRole[r].length} employees</span>
                </Button>
              )) : <p className="text-center text-muted-foreground py-4">No employees available</p>}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setRole("")} className="text-muted-foreground">← Back</Button>
              <span className="font-medium">{role}</span>
            </div>
            <div className="grid gap-2 max-h-[50vh] overflow-y-auto" style={{ overscrollBehavior: "contain" }}>
              {[...employeesByRole[role]].sort(byName).map((e) => <EmployeeButton key={e.id} emp={{ ...e, role }} selected={e.id === value} onPick={pick} testId={`${testIdPrefix}-option-${e.id}`} />)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmployeePickerDialog;
